'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { cx, getDownloadURL } from '@/utils';
import Button from '@common/Button';
import CustomLink from '@common/CustomLink';
import DotButton, { useDotButton } from '@common/DotButton/DotButton';
import SvgArrow from '@common/Icons/Arrow';
import SvgPause from '@common/Icons/Pause';
import SvgPlay from '@common/Icons/Play';
import { useHomeContext } from '@views/Home/HomeProvider/HomeContext';
import { EmblaCarouselType } from 'embla-carousel';
import Autoplay, { AutoplayType } from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';

import {
  PageBlockStyles,
  PageSliderBlock,
} from '@/components/common/Providers/GraphqlProvider/generated';
import { Typography } from '@/components/ui/Typography';

import styles from './styles.module.css';

/* =========================================================
 * COMPONENTE PRINCIPAL: Banner
 * ========================================================= */
const Banner = ({ currentSliderBlock }: { currentSliderBlock?: PageSliderBlock }) => {
  const [isPlaying, setIsPlaying] = useState(true);

  /* -------- obtener bloque desde props o context ---------- */
  const {
    home: { sliderBlock: sliderBlockContext },
  } = useHomeContext();
  const sliderBlock = currentSliderBlock || sliderBlockContext;

  /* --------------- instancia única de Autoplay ------------ */
  const autoplayRef = useRef<AutoplayType | null>(null);
  if (!autoplayRef.current) {
    autoplayRef.current = Autoplay({
      delay: 4000,
      stopOnInteraction: false,
      stopOnMouseEnter: false,
    });
  }

  /* ----------- pasamos plugins solo si hace falta ---------- */
  const plugins = (sliderBlock?.sliders?.length ?? 0) > 1 ? [autoplayRef.current] : [];
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true }, plugins);

  /* ------------ play / stop cuando Embla esté listo -------- */
  useEffect(() => {
    if (!emblaApi) return;
    const autoplay = emblaApi.plugins().autoplay as AutoplayType | undefined;
    if (!autoplay) return;
    isPlaying ? autoplay.play() : autoplay.stop();
  }, [isPlaying, emblaApi]);

  /* ---------------- limpieza defensiva -------------------- */
  useEffect(() => () => autoplayRef.current?.stop(), []);

  /* --------------- handlers de navegación ----------------- */
  const togglePlay = useCallback(() => setIsPlaying((prev) => !prev), []);

  if (sliderBlock?.isDisabled) return null;
  if (!sliderBlock?.sliders?.length) return null;

  return (
    <section className="relative">
      {/* Flecha anterior */}
      {/* Flecha anterior */}
      <button
        className={cx(
          'absolute inset-y-0 z-10 my-auto flex items-center justify-center',
          'left-3 h-[42px] w-[42px] rotate-180 rounded-full bg-[#4b4b4b]/35',
          'md:left-14 md:my-0 md:block md:h-auto md:w-auto md:rounded-none md:bg-transparent'
        )}
        onClick={() => emblaApi?.scrollPrev()}
      >
        <SvgArrow className="block h-4 w-4 text-white md:hidden" />
        <SvgArrow className={cx('hidden md:block', styles.handlers)} />
      </button>

      {/* Flecha siguiente */}
      <button
        className={cx(
          'absolute inset-y-0 z-10 my-auto flex items-center justify-center',
          'right-3 h-[42px] w-[42px] rounded-full bg-[#4b4b4b]/35',
          'md:right-14 md:my-0 md:block md:h-auto md:w-auto md:rounded-none md:bg-transparent'
        )}
        onClick={() => emblaApi?.scrollNext()}
      >
        <SvgArrow className="block h-4 w-4 text-white md:hidden" />
        <SvgArrow className={cx('hidden md:block', styles.handlers)} />
      </button>

      {/* Botón pausa/reanudar + dots */}
      <div className={styles.dotsWrapper}>
        <PauseResumeButton isPlaying={isPlaying} onToggle={togglePlay} />
        <DotsNavigation emblaApi={emblaApi} sliderBlock={sliderBlock} />
      </div>

      {/* Carrusel */}
      <div className="overflow-hidden" ref={emblaRef}>
        <SlidesList sliderBlock={sliderBlock} />
      </div>
    </section>
  );
};

/* =========================================================
 * SUBCOMPONENTE: Botón Pausa/Reanudar
 * ========================================================= */
function PauseResumeButton({ isPlaying, onToggle }: { isPlaying: boolean; onToggle: () => void }) {
  return (
    <button type="button" className="flex flex-row items-center gap-2" onClick={onToggle}>
      {isPlaying ? (
        <>
          <SvgPause className="text-white drop-shadow-md" stroke="#737373" />
          <span className="text-white drop-shadow-md">{'Pausar'}</span>
        </>
      ) : (
        <>
          <SvgPlay className="text-white drop-shadow-md" stroke="#737373" />
          <span className="text-white drop-shadow-md">{'Reanudar'}</span>
        </>
      )}
    </button>
  );
}

/* =========================================================
 * SUBCOMPONENTE: Navegación (Dots)
 * ========================================================= */
function DotsNavigation({
  emblaApi,
  sliderBlock,
}: {
  emblaApi: EmblaCarouselType | undefined;
  sliderBlock: PageSliderBlock;
}) {
  const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(emblaApi);

  if (sliderBlock.pageBlockStyle === PageBlockStyles.A) return null;

  return (
    <div className={styles.dots}>
      {scrollSnaps.map((_, index) => (
        <DotButton
          key={index}
          onClick={() => onDotButtonClick(index)}
          className={cx(styles.dot, {
            [styles.selected]: index === selectedIndex,
          })}
        />
      ))}
    </div>
  );
}

/* =========================================================
 * SUBCOMPONENTE: Listado de Slides
 * ========================================================= */
function SlidesList({ sliderBlock }: { sliderBlock: PageSliderBlock }) {
  const sliders = [...(sliderBlock?.sliders ?? [])].sort(
    (a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0)
  );

  return (
    <div className={styles.slides}>
      {(sliders ?? []).map((slide, key) => (
        <div className="min-w-0 flex-[0_0_100%]" key={key}>
          <div className="relative h-[220px] w-full md:aspect-[4/1] md:h-auto">
            {slide?.image?.id ? (
              <>
                {/* Background image with blur */}
                <Image
                  src={getDownloadURL(slide?.image?.id)}
                  alt={slide.title || 'Banner'}
                  fill
                  style={{ objectFit: 'cover', filter: 'blur(8px)' }}
                  priority={key === 0}
                />
                {/* Main image on top */}
                <CustomLink pageLink={slide.linkItem}>
                  <Image
                    src={getDownloadURL(slide?.image?.id)}
                    alt={slide.title || 'Banner'}
                    fill
                    quality={100}
                    style={{ objectFit: 'contain' }}
                    priority={key === 0}
                  />
                </CustomLink>
              </>
            ) : null}

            {/* Info visible salvo estilo A */}
            {sliderBlock.pageBlockStyle !== PageBlockStyles.A && (
              <div
                className={cx(styles.info, {
                  [styles.infoc]: sliderBlock.pageBlockStyle === PageBlockStyles.B,
                })}
              >
                <Typography variant="h2" className={cx(styles.title, '!text-white')}>
                  {slide.title}
                </Typography>

                <ScrollingTags tags={slide.tags} />

                <Typography
                  variant="body1"
                  className="mt-3 h-fit overflow-hidden overflow-y-auto text-white"
                >
                  {slide.description}
                </Typography>

                {sliderBlock.pageBlockStyle !== PageBlockStyles.B && (
                  <div className="mt-5 pb-10 md:mt-5 md:pb-0">
                    <CustomLink pageLink={slide.linkItem}>
                      <Button
                        text={'Ver más'}
                        backgroundColor="bg-white hover:bg-white/80"
                        textClassname="text-primary"
                      />
                    </CustomLink>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

/* =========================================================
 * SUBCOMPONENTE: ScrollingTags
 * ========================================================= */
function ScrollingTags({ tags }: { tags: string[] }) {
  const tagsRef = useRef<HTMLDivElement>(null);
  const [isPaused, setIsPaused] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [scrollDirection, setScrollDirection] = useState(1);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const velocity = 500;

  const checkOverflow = useCallback(() => {
    const container = tagsRef.current;
    if (container) {
      setShouldAnimate(container.scrollWidth > container.clientWidth);
    }
  }, []);

  useEffect(() => {
    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [checkOverflow]);

  const autoScroll = useCallback(() => {
    const container = tagsRef.current;
    if (!container || isPaused || isDragging || !shouldAnimate) return;

    const maxScroll = container.scrollWidth - container.clientWidth;

    if (scrollDirection === 1 && container.scrollLeft >= maxScroll) {
      setScrollDirection(-1);
    } else if (scrollDirection === -1 && container.scrollLeft <= 0) {
      setScrollDirection(1);
    }

    container.scrollLeft += scrollDirection;
  }, [isPaused, isDragging, scrollDirection, shouldAnimate]);

  useEffect(() => {
    const intervalId = setInterval(autoScroll, velocity);
    return () => clearInterval(intervalId);
  }, [autoScroll]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!shouldAnimate) return;
    setIsDragging(true);
    setStartX(e.pageX - (tagsRef.current?.offsetLeft ?? 0));
    setScrollLeft(tagsRef.current?.scrollLeft ?? 0);
  };
  const handleMouseLeave = () => setIsDragging(false);
  const handleMouseUp = () => setIsDragging(false);
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !shouldAnimate) return;
    e.preventDefault();
    const x = e.pageX - (tagsRef.current?.offsetLeft ?? 0);
    const walk = (x - startX) * 3;
    if (tagsRef.current) {
      tagsRef.current.scrollLeft = scrollLeft - walk;
    }
  };

  return (
    <div
      className={styles.tagsWrapper}
      onMouseEnter={() => shouldAnimate && setIsPaused(true)}
      onMouseLeave={() => {
        setIsPaused(false);
        handleMouseLeave();
      }}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseMove={handleMouseMove}
    >
      <div ref={tagsRef} className={styles.tagsContainer}>
        {tags.map((tag, index) => (
          <div key={index} className={styles.nameWrapper}>
            <div className="h-[10px] w-[10px] rounded-full bg-white" />
            <span className={styles.name}>{tag}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* =========================================================
 * SKELETON: BannerSkeleton
 * Simple placeholder that resembles the banner height and controls
 * ========================================================= */
export function BannerSkeleton() {
  return (
    <section className="relative animate-pulse">
      <div className="h-[220px] w-full bg-gray-200 md:aspect-[4/1] md:h-auto" />
    </section>
  );
}

export default Banner;
